import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Pagination, Select, TextField, Typography } from '@mui/material';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { SelectResult } from '../../../components/select/select-chain.component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponseForPost, ApiResponseWithPagination } from '../../../models/api-response';
import { WorkorderItem } from './workorder-item.component';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import useDebounce from '../../../hooks/use-debounce';
import { useSaveData, useSaveDataForPost } from '../../../hooks/useSaveData';

interface WorkOrdersTabProps {
  selectedFilters?: SelectResult;
  handleWorkOrderRefresh?: (value: boolean) => void;
  isWorkOrderRefresh: boolean;
  handlePageNumberOrPageSizeChange?: (pageNumber: number, pageSize: number) => void;
  searchParams?: any;
}

const ITEMS_PER_PAGE = 10;

export const WorkOrdersTab: React.FC<WorkOrdersTabProps> = ({
  selectedFilters,
  handleWorkOrderRefresh,
  isWorkOrderRefresh,
  handlePageNumberOrPageSizeChange,
  searchParams,
}) => {
  const [currentPage, setCurrentPage] = useState(selectedFilters?.pageNumber || 1);
  const [itemsPerPage, setItemsPerPage] = useState(selectedFilters?.pageSize || ITEMS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data, isLoading, mutateAsync } = useSaveDataForPost<ApiResponseForPost<any>>(
    workOrdersApiKeyStore.getWorkOrderByFilters()
  );

  useEffect(() => {
    if (isWorkOrderRefresh) {
      const filterPayload = {
        ...selectedFilters,
        workOrderID: selectedFilters?.workOrderID ?? 0,
        workOrderStatusIDs: !Array?.isArray(selectedFilters?.workOrderStatusIDs)
          ? selectedFilters?.workOrderStatusIDs?.split(",").map((s: any) => Number(s?.trim()))
          : selectedFilters?.workOrderStatusIDs,
        services: !Array?.isArray(selectedFilters?.services)
          ? selectedFilters?.services?.split(",").map((s: any) => Number(s?.trim()))
          : selectedFilters?.services,
      };

      mutateAsync({ data: filterPayload });
      handleWorkOrderRefresh?.(false);
    }
  }, [isWorkOrderRefresh, handleWorkOrderRefresh]);

  useEffect(() => {
    if (currentPage !== 1 && itemsPerPage !== 10) {
      const updatedFilters = {
        ...selectedFilters,
        workOrderStatusIDs: Array.isArray(selectedFilters?.workOrderStatusIDs)
          ? selectedFilters?.workOrderStatusIDs // If already an array, use it as-is
          : selectedFilters?.workOrderStatusIDs
            ? selectedFilters?.workOrderStatusIDs?.split(',')?.map(Number) // If a string, convert to an array of numbers
            : [], // Default to an empty array if undefined or null
        services: Array.isArray(selectedFilters?.services)
          ? selectedFilters?.services // If already an array, use it as-is
          : selectedFilters?.services
            ? selectedFilters?.services?.split(',')?.map(Number) // If a string, convert to an array of numbers
            : [], // Default to an empty array if undefined or null
      };

      mutateAsync({ data: updatedFilters });
      handleWorkOrderRefresh?.(false);
    }
  }, [currentPage, itemsPerPage]);

  const refetchData = () => {
    mutateAsync({ data: selectedFilters });
  };

  const handlePageChange = (event: any, value: number) => {
    setCurrentPage(value);
    handlePageNumberOrPageSizeChange?.(value, selectedFilters?.pageSize || ITEMS_PER_PAGE); // Reset to first page when items per page change
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value as number);
    setCurrentPage(1);
    handlePageNumberOrPageSizeChange?.(1, event.target.value as number); // Reset to first page when items per page change
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    // setCurrentPage(1); // Reset to first page when search query changes
  };

  const workOrderData: any = data?.data;

  const filteredData = useMemo(() => {
    if (!workOrderData) return [];
    return workOrderData.filter(
      (item: any) =>
        item.workOrderNumber?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        item.workOrderStatus?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        item.inspectorName?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        item.serviceName?.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  }, [debouncedSearchQuery, workOrderData]);

  const handleFooter = (pageNumber: any, pageSize: any, totalRecords: any) => {
    const startIndex = (pageNumber - 1) * pageSize + 1;
    const endIndex = Math.min(pageNumber * pageSize, totalRecords);
    const footerText = `${startIndex} - ${endIndex} of ${totalRecords} ${totalRecords <= 1 ? 'item' : 'items'}`;
    return footerText;
  }

  return (
    <div style={{ padding: '20px 15px 10px' }}>
      <h3>Work Orders</h3>
      <div>
        <LoadingIndicator isLoading={isLoading} isSpinner />
        {filteredData?.map((item: any) => (
          <WorkorderItem
            isCompleted={item?.isUnitCompleted}
            key={item.workOrderID}
            item={item}
            reloadData={refetchData}
          />
        ))}
      </div>
      <LoadingIndicator isLoading={isLoading} />
      {filteredData?.length > 0 && (
        <Grid style={{ padding: '20px 0px 15px' }} container spacing={0}>
          <Grid item lg={10} sm={10} xl={10} xs={10}>

            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
            >

              <Pagination
                count={data?.totalPages}
                page={selectedFilters?.pageNumber || 1}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
              />
              <FormControl className="pagingperSelect" variant="outlined">
                <Select
                  labelId="items-per-page-label"
                  id="items-per-page-select"
                  value={selectedFilters?.pageSize || ITEMS_PER_PAGE}
                  onChange={handleItemsPerPageChange}
                  label=" "
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
                <span>items per page</span>
              </FormControl>
            </Grid>
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2} style={{ paddingTop: 8 }}>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "flex-end",
                alignItems: "flex-start",
              }}
            >
              <span>{handleFooter(data?.pageNumber, data?.pageSize, data?.totalRecords)}</span>
              {/* <span>{selectedFilters?.pageNumber || 1} - {selectedFilters?.pageSize || ITEMS_PER_PAGE} of {filteredData?.length} {`${filteredData?.length <= 1 ? 'item' : 'items'}`}</span> */}
            </Grid>
          </Grid>
        </Grid>
      )
      }
    </div >
  );
};
